/**
 * Simple moving average aligned to a close series.
 *
 * Warmup bars and any window that contains a non-finite close are `null`,
 * never 0 — a missing average is not an average of zero (R2b).
 *
 * The rolling-sum pass is a local accumulator. `functional/no-let` would force
 * `bars.slice()` per bar (O(n·period) time and allocations). A poisoned bar
 * that later leaves the window must recover — that is the invariant the
 * recovery test locks, and the reason the sum tracks a bad-count instead of
 * adding NaN into the running total.
 */
export function requireAveragePeriod(period: number): void {
  if (!Number.isInteger(period) || period < 1) {
    throw new RangeError(`period must be a positive integer (got ${String(period)})`);
  }
}

function meanOfSum(sum: number, period: number): number | null {
  const mean = sum / period;
  return Number.isFinite(mean) ? mean : null;
}

function finiteDelta(value: number | undefined): { readonly add: number; readonly bad: 0 | 1 } {
  if (value !== undefined && Number.isFinite(value)) return { add: value, bad: 0 };
  return { add: 0, bad: 1 };
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

function windowMeanAt(bars: readonly number[], end: number, period: number): number | null {
  const start = end - period + 1;
  if (start < 0) return null;
  let sum = 0;
  for (let i = start; i <= end; i++) {
    const value = bars[i];
    if (value === undefined || !Number.isFinite(value)) return null;
    sum += value;
  }
  return meanOfSum(sum, period);
}

export function simpleMovingAverage(
  bars: readonly number[],
  period: number,
): readonly (number | null)[] {
  requireAveragePeriod(period);
  const values: (number | null)[] = [];
  let sum = 0;
  let badCount = 0;
  for (let i = 0; i < bars.length; i++) {
    const incoming = finiteDelta(bars[i]);
    sum += incoming.add;
    badCount += incoming.bad;
    if (i >= period) {
      const outgoing = finiteDelta(bars[i - period]);
      sum -= outgoing.add;
      badCount -= outgoing.bad;
    }
    const ready = i >= period - 1 && badCount === 0;
    values.push(ready ? meanOfSum(sum, period) : null);
  }
  return values;
}

/** Last formed window mean, scanning from the end. Empty / none formed → null. */
export function lastSimpleMovingAverage(bars: readonly number[], period: number): number | null {
  requireAveragePeriod(period);
  for (let end = bars.length - 1; end >= period - 1; end--) {
    const mean = windowMeanAt(bars, end, period);
    if (mean !== null) return mean;
  }
  return null;
}

/** Most recent defined value, scanning from the end. Empty / all-null → null. */
export function lastDefined(values: readonly (number | null)[]): number | null {
  for (let i = values.length - 1; i >= 0; i--) {
    const value = values[i];
    if (value !== null && value !== undefined && Number.isFinite(value)) return value;
  }
  return null;
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */
